import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, FileDoc, CalendarBlank, Calendar, ChartBar, FloppyDisk, Trash, BookmarkSimple, ShieldWarning } from "@phosphor-icons/react";
import { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, Legend } from "recharts";

const KINDS = [
  { key: "daily",   label: "Daily",   icon: CalendarBlank, desc: "Today’s transactions and requests." },
  { key: "weekly",  label: "Weekly",  icon: Calendar,      desc: "Consolidated activity over the last 7 days." },
  { key: "monthly", label: "Monthly", icon: ChartBar,      desc: "Full performance for the last 30 days." },
  { key: "custom",  label: "Custom Range", icon: Calendar, desc: "Pick your own start and end date." },
];

const MODULES = [
  { key: "all",              label: "All Modules" },
  { key: "budgets",          label: "Requests & Vouchers" },
  { key: "check_vouchers",   label: "Check Vouchers" },
  { key: "quotations",       label: "Quotations" },
  { key: "purchase_orders",  label: "Purchase Orders" },
  { key: "billing",          label: "Billing Requests" },
  { key: "receivables",      label: "Receivables",     restricted: ["admin", "finance"] },
  { key: "liquidations",     label: "Liquidations" },
  { key: "bank_reconciliations", label: "Bank Reconciliations", restricted: ["admin", "finance"] },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [active, setActive] = useState("daily");
  const [module, setModule] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [beginningBalance, setBeginningBalance] = useState("");
  const [additionalFund, setAdditionalFund] = useState("");
  const [prefillNote, setPrefillNote] = useState("");
  const [savingBalance, setSavingBalance] = useState(false);
  const autoSavedRef = useRef(null);
  // Saved presets (module + kind + custom range) private to the current user.
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  // Category trend chart — populated on the Monthly tab for Admin/Finance.
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    if (active !== "monthly" || !(user?.role === "admin" || user?.role === "finance")) {
      setTrend(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/reports/category-trend", { params: { months: 6, top: 5 } });
        if (!cancelled) setTrend(data);
      } catch {
        if (!cancelled) setTrend(null);
      }
    })();
    return () => { cancelled = true; };
  }, [active, user?.role]);

  async function downloadBIR() {
    const t = new Date();
    const month = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
    const picked = window.prompt("BIR 2550-M — pick month (YYYY-MM):", month);
    if (!picked || !/^\d{4}-(0[1-9]|1[0-2])$/.test(picked)) {
      if (picked) toast.error("Month must look like 2026-08");
      return;
    }
    try {
      await downloadFile(`/reports/bir-2550m/pdf?month=${picked}`, `bir_2550m_draft_${picked}.pdf`);
      toast.success(`BIR 2550-M draft downloaded — ${picked}`);
    } catch { toast.error("Download failed"); }
  }

  async function downloadEWT() {
    const t = new Date();
    const month = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
    const picked = window.prompt("Monthly EWT Report — pick month (YYYY-MM):", month);
    if (!picked || !/^\d{4}-(0[1-9]|1[0-2])$/.test(picked)) {
      if (picked) toast.error("Month must look like 2026-08");
      return;
    }
    try {
      await downloadFile(`/reports/ewt-summary/pdf?month=${picked}`, `ewt_summary_${picked}.pdf`);
      toast.success(`EWT summary downloaded — ${picked}`);
    } catch { toast.error("Download failed"); }
  }

  async function downloadOffsetReport() {
    const t = new Date();
    const month = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
    const picked = window.prompt("Monthly Offset Report — pick month (YYYY-MM):", month);
    if (!picked || !/^\d{4}-(0[1-9]|1[0-2])$/.test(picked)) {
      if (picked) toast.error("Month must look like 2026-08");
      return;
    }
    try {
      await downloadFile(`/reports/offset-summary/pdf?month=${picked}`, `offset_summary_${picked}.pdf`);
      toast.success(`Offset summary downloaded — ${picked}`);
    } catch { toast.error("Download failed"); }
  }

  async function downloadDisposalRegister() {
    const t = new Date();
    const month = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`;
    const picked = window.prompt("Monthly Disposal Register — pick month (YYYY-MM):", month);
    if (!picked || !/^\d{4}-(0[1-9]|1[0-2])$/.test(picked)) {
      if (picked) toast.error("Month must look like 2026-08");
      return;
    }
    try {
      await downloadFile(`/reports/disposal-register/pdf?month=${picked}`, `disposal_register_${picked}.pdf`);
      toast.success(`Disposal register downloaded — ${picked}`);
    } catch { toast.error("Download failed"); }
  }

  async function loadPresets() {
    try {
      const { data } = await api.get("/report-presets");
      setPresets(data || []);
    } catch { /* silent — presets are non-critical */ }
  }
  useEffect(() => { loadPresets(); }, []);

  async function saveCurrentAsPreset() {
    const name = window.prompt(
      "Name this preset (e.g. “Weekly Finance”, “August BIR”):",
      MODULES.find((m) => m.key === module)?.label + " · " + KINDS.find((k) => k.key === active)?.label,
    );
    if (!name || !name.trim()) return;
    // Admins get an extra prompt to publish this preset to the whole Finance
    // team. Non-admins never see it — server also enforces this.
    let shared = false;
    if (user?.role === "admin") {
      shared = window.confirm(
        "Share this preset with the Finance team?\n\n" +
        "OK   → Everyone in Finance sees it in their preset bar.\n" +
        "Cancel → Keep it private (only you see it)."
      );
    }
    setSavingPreset(true);
    try {
      const payload = { name: name.trim(), module, kind: active, shared_with_finance: shared };
      if (active === "custom") { payload.start_date = startDate; payload.end_date = endDate; }
      const { data } = await api.post("/report-presets", payload);
      setPresets((prev) => [data, ...prev]);
      toast.success(`Saved preset “${data.name}”${data.shared_with_finance ? " · shared with Finance" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingPreset(false);
    }
  }

  function applySavedPreset(p) {
    setModule(p.module);
    setActive(p.kind);
    if (p.kind === "custom") {
      if (p.start_date) setStartDate(p.start_date);
      if (p.end_date) setEndDate(p.end_date);
    }
    toast.success(`Applied preset “${p.name}”`);
  }

  async function removeSavedPreset(p) {
    if (!window.confirm(`Delete preset “${p.name}”?`)) return;
    try {
      await api.delete(`/report-presets/${p.id}`);
      setPresets((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  const availableModules = MODULES.filter((m) => !m.restricted || m.restricted.includes(user?.role));

  // Compute period start & end dates for the currently selected tab.
  function periodBounds() {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (active === "custom") return { start: startDate, end: endDate };
    if (active === "daily") return { start: iso(t), end: iso(t) };
    const back = active === "weekly" ? 7 : 30;
    const s = new Date(Date.now() - back * 86400000);
    return { start: iso(s), end: iso(t) };
  }

  function rangeParams() {
    if (active !== "custom") return {};
    if (!startDate || !endDate) return {};
    return { start_date: startDate, end_date: endDate };
  }

  function balanceParam() {
    if (module !== "budgets") return {};
    const p = {};
    const b = parseFloat(beginningBalance);
    if (Number.isFinite(b)) p.beginning_balance = b;
    const a = parseFloat(additionalFund);
    if (Number.isFinite(a) && a !== 0) p.additional_fund = a;
    return p;
  }

  async function load(kind, mod) {
    if (kind === "custom" && (!startDate || !endDate)) return;
    setLoading(true);
    try {
      const params = { module: mod, ...rangeParams(), ...balanceParam() };
      // For custom, use "monthly" as the underlying kind since the server ignores it when dates are given
      const kindPath = kind === "custom" ? "monthly" : kind;
      const { data } = await api.get(`/reports/${kindPath}/data`, { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report");
      setData(null);
    } finally { setLoading(false); }
  }

  // Auto-prefill Beginning Balance from the most recent saved closing before this period.
  async function tryPrefillOpening() {
    if (module !== "budgets") return;
    if (beginningBalance !== "" && !prefillNote) return;   // never overwrite a value the user typed
    const { start } = periodBounds();
    if (!start) return;
    try {
      const { data } = await api.get("/settings/cash-position", { params: { before_date: start } });
      if (data && typeof data.closing_balance === "number") {
        setBeginningBalance(String(data.closing_balance));
        setPrefillNote(`Carried over from ${data.as_of_date} closing (₱${data.closing_balance.toLocaleString()}${data.carried_to && data.carried_to.slice(0, 7) !== data.as_of_date.slice(0, 7) ? " — across month change" : ""}).`);
      } else {
        setBeginningBalance("");
        setPrefillNote("");
      }
    } catch { /* no prefill */ }
  }

  useEffect(() => { load(active, module); /* eslint-disable-next-line */ }, [active, module, startDate, endDate, beginningBalance, additionalFund]);
  useEffect(() => { tryPrefillOpening(); /* eslint-disable-next-line */ }, [active, module, startDate, endDate]);

  // Auto-persist the closing balance whenever it is computed, so tomorrow's opening
  // pre-fills automatically without the user needing to click "Save closing" every day.
  // Silent — no toast on success; 403s (non-finance users) are swallowed.
  useEffect(() => {
    if (module !== "budgets") return;
    if (active !== "daily") return;                 // only daily auto-saves; period reports auto-derive from these snapshots
    const closing = currentClosingNumeric();
    if (closing === null) return;
    const { end } = periodBounds();
    if (!end) return;
    const addF = parseFloat(additionalFund);
    const addFundVal = Number.isFinite(addF) ? addF : 0;
    const uid = `${end}:${closing.toFixed(2)}:${addFundVal.toFixed(2)}`;
    if (autoSavedRef.current === uid) return;      // avoid re-saving same value
    autoSavedRef.current = uid;
    api.put("/settings/cash-position", { as_of_date: end, closing_balance: closing, additional_fund: addFundVal }).catch(() => {});
    /* eslint-disable-next-line */
  }, [data, module, active, additionalFund]);

  // Extract numeric closing balance from summary (e.g. "₱ 48,300.00").
  function currentClosingNumeric() {
    const raw = data?.summary?.["Closing Balance"];
    if (!raw) return null;
    const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  async function saveClosingAsNextOpening() {
    const closing = currentClosingNumeric();
    if (closing === null) { toast.error("No Closing Balance to save yet"); return; }
    const { end } = periodBounds();
    if (!end) { toast.error("Missing period end date"); return; }
    setSavingBalance(true);
    try {
      await api.put("/settings/cash-position", { as_of_date: end, closing_balance: closing });
      toast.success(`Saved ₱${closing.toLocaleString()} as opening for periods starting after ${end}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingBalance(false); }
  }

  async function download(fmt) {
    try {
      const suffix = module === "all" ? "" : `_${module}`;
      const kindPath = active === "custom" ? "monthly" : active;
      const params = new URLSearchParams({ module });
      if (active === "custom" && startDate && endDate) {
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      }
      if (module === "budgets") {
        const n = parseFloat(beginningBalance);
        if (Number.isFinite(n)) params.append("beginning_balance", String(n));
        const a = parseFloat(additionalFund);
        if (Number.isFinite(a) && a !== 0) params.append("additional_fund", String(a));
      }
      const url = `/reports/${kindPath}/${fmt}?${params.toString()}`;
      const rangeSuffix = active === "custom" ? `_${startDate}_to_${endDate}` : "";
      await downloadFile(url, `${active}${suffix}${rangeSuffix}_report.${fmt === "pdf" ? "pdf" : "docx"}`);
      toast.success(`Downloaded ${active} ${module === "all" ? "" : MODULES.find((m) => m.key === module)?.label + " "}report`);
    } catch { toast.error("Download failed"); }
  }

  // ---------- Custom-range presets ----------
  function applyPreset(key) {
    const iso = (d) => d.toISOString().slice(0, 10);
    const now = new Date();
    let s, e;
    if (key === "this_month") {
      s = new Date(now.getFullYear(), now.getMonth(), 1);
      e = now;
    } else if (key === "last_month") {
      s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      e = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
    } else if (key === "this_quarter") {
      const q = Math.floor(now.getMonth() / 3);
      s = new Date(now.getFullYear(), q * 3, 1);
      e = now;
    } else if (key === "last_quarter") {
      const q = Math.floor(now.getMonth() / 3);
      const lastQStartMonth = (q - 1) * 3;
      const y = lastQStartMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = ((lastQStartMonth % 12) + 12) % 12;
      s = new Date(y, m, 1);
      e = new Date(y, m + 3, 0);
    } else if (key === "ytd") {
      s = new Date(now.getFullYear(), 0, 1);
      e = now;
    } else {
      return;
    }
    setStartDate(iso(s));
    setEndDate(iso(e));
    setActive("custom");
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Reports</div>
          <h1 className="section-title text-3xl sm:text-4xl">Automated Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Pick a module to generate a focused report — or "All Modules" for the consolidated view.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="min-w-[220px]">
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger data-testid="report-module-select" className="border-border">
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                {availableModules.map((m) => (
                  <SelectItem key={m.key} value={m.key} data-testid={`report-module-option-${m.key}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => download("pdf")} data-testid="report-download-pdf"><FilePdf size={16} className="mr-2" /> Download PDF</Button>
          <Button variant="outline" onClick={() => download("docx")} data-testid="report-download-docx" className="border-border"><FileDoc size={16} className="mr-2" /> Download Word</Button>
          {(user?.role === "admin" || user?.role === "finance") && (
            <Button variant="outline" onClick={downloadBIR} data-testid="report-bir-2550m-btn" className="border-border">
              <FilePdf size={16} className="mr-2" /> BIR 2550-M Draft
            </Button>
          )}
          {(user?.role === "admin" || user?.role === "finance") && (
            <Button variant="outline" onClick={downloadEWT} data-testid="report-ewt-summary-btn" className="border-border">
              <FilePdf size={16} className="mr-2" /> Monthly EWT
            </Button>
          )}
          {(user?.role === "admin" || user?.role === "finance") && (
            <Button variant="outline" onClick={downloadOffsetReport} data-testid="report-offset-summary-btn" className="border-border">
              <FilePdf size={16} className="mr-2" /> Monthly Offsets
            </Button>
          )}
          {["admin", "finance", "procurement", "inventory"].includes(user?.role) && (
            <Button variant="outline" onClick={downloadDisposalRegister} data-testid="report-disposal-register-btn" className="border-border">
              <FilePdf size={16} className="mr-2" /> Disposal Register
            </Button>
          )}
        </div>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="border border-border">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`report-tab-${k.key}`}>
              <k.icon size={14} className="mr-2" /> {k.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Saved presets — private per-user shortcuts for recurring filings. */}
        <div className="mt-3 flex flex-wrap items-center gap-2 p-3 border border-dashed border-border rounded-md bg-muted/20" data-testid="saved-presets-bar">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <BookmarkSimple size={12} /> My Presets
          </div>
          {presets.length === 0 ? (
            <span className="text-xs text-muted-foreground">None saved yet — pick filters and hit Save.</span>
          ) : presets.map((p) => {
            const mine = p.owner_id === user?.id;
            const canDelete = mine || user?.role === "admin";
            return (
              <div key={p.id} className="group flex items-center gap-1 rounded-full border border-border bg-background hover:bg-muted transition-colors" data-testid={`saved-preset-${p.id}`}>
                <button
                  type="button"
                  onClick={() => applySavedPreset(p)}
                  className="text-xs pl-2.5 pr-1 py-1 flex items-center gap-1.5"
                  title={`${MODULES.find((m) => m.key === p.module)?.label || p.module} · ${KINDS.find((k) => k.key === p.kind)?.label || p.kind}${p.kind === "custom" ? ` · ${p.start_date} → ${p.end_date}` : ""}${p.shared_with_finance ? ` · shared by ${p.owner_name}` : ""}`}
                >
                  {p.shared_with_finance && <Users size={11} className="text-emerald-700" />}
                  <span>{p.name}</span>
                  {p.shared_with_finance && !mine && (
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">shared</span>
                  )}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => removeSavedPreset(p)}
                    className="text-muted-foreground hover:text-rose-600 pr-2 py-1"
                    title={mine ? "Delete preset" : "Delete (admin)"}
                    data-testid={`saved-preset-delete-${p.id}`}
                  >
                    <Trash size={11} />
                  </button>
                )}
              </div>
            );
          })}
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="border-border"
            onClick={saveCurrentAsPreset}
            disabled={savingPreset || (active === "custom" && (!startDate || !endDate))}
            data-testid="save-preset-btn"
          >
            <FloppyDisk size={12} className="mr-1.5" />
            Save current
          </Button>
        </div>

        {module === "budgets" && (
          <div className="flex items-center justify-end mt-3">
            <Button
              variant="outline"
              className="border-border"
              onClick={async () => {
                const { start, end } = periodBounds();
                if (!start || !end) { toast.error("Pick a valid date range"); return; }
                try {
                  await downloadFile(`/reports/cash-position/pdf?start_date=${start}&end_date=${end}`, `cash_position_${start}_to_${end}.pdf`);
                  toast.success("Cash Position sheet downloaded");
                } catch { toast.error("Download failed"); }
              }}
              data-testid="download-cash-position-pdf-btn"
            >
              <FilePdf size={14} className="mr-2" /> Cash Position Sheet (PDF)
            </Button>
          </div>
        )}

        {active === "custom" && (
          <div className="mt-4 flex flex-col gap-3 p-4 border border-border rounded-md bg-muted/30" data-testid="report-custom-range">
            <div className="flex flex-wrap gap-2" data-testid="report-range-presets">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground self-center mr-1">Quick range:</span>
              {[
                { key: "this_month", label: "This Month" },
                { key: "last_month", label: "Last Month" },
                { key: "this_quarter", label: "This Quarter" },
                { key: "last_quarter", label: "Last Quarter" },
                { key: "ytd", label: "Year to Date" },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className="text-xs px-2.5 py-1 rounded-full border border-border bg-background hover:bg-muted transition-colors"
                  data-testid={`report-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Date</Label>
                <Input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} className="border-border" data-testid="report-start-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">End Date</Label>
                <Input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} className="border-border" data-testid="report-end-date" />
              </div>
              <div className="text-xs text-muted-foreground pb-2">
                Records are filtered by <span className="font-mono">created_at</span> between these dates (inclusive).
              </div>
            </div>
          </div>
        )}

        {module === "budgets" && active === "daily" && (
          <div className="mt-4 flex flex-wrap items-end gap-3 p-4 border border-border rounded-md bg-muted/30" data-testid="report-cash-position">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Beginning Balance (₱)</Label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="e.g. 100000.00"
                value={beginningBalance}
                onChange={(e) => { setBeginningBalance(e.target.value); setPrefillNote(""); }}
                className="border-border w-48"
                data-testid="report-beginning-balance"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Additional Fund (₱)</Label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={additionalFund}
                onChange={(e) => setAdditionalFund(e.target.value)}
                className="border-border w-40"
                data-testid="report-additional-fund"
              />
            </div>
            {(beginningBalance !== "" || additionalFund !== "") && (
              <Button type="button" variant="ghost" className="border border-border pb-2" onClick={() => { setBeginningBalance(""); setAdditionalFund(""); setPrefillNote(""); }} data-testid="clear-beginning-balance-btn">
                Clear
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="border-border pb-2"
              disabled={savingBalance || currentClosingNumeric() === null}
              onClick={saveClosingAsNextOpening}
              data-testid="save-closing-balance-btn"
              title={currentClosingNumeric() === null ? "Enter a beginning balance first" : "Persist current closing as the next period's opening"}
            >
              {savingBalance ? "Saving…" : "Save closing as next opening"}
            </Button>
            <div className="text-xs text-muted-foreground pb-2 max-w-md">
              Input-only. <b>Total Available = Beginning + Additional Fund</b>, then <b>Closing = Total Available − Outflow (OR + CV)</b>. Check Vouchers are excluded. Use Additional Fund whenever cash is topped-up mid-period. Closing is auto-saved — tomorrow's opening pre-fills automatically.
              {prefillNote && <div className="mt-1 text-emerald-700 dark:text-emerald-500">{prefillNote}</div>}
            </div>
          </div>
        )}

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key} className="mt-4">
            <div className="text-sm text-muted-foreground mb-4">{k.desc}</div>
            {loading || !data ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <div className="space-y-6">
                {/* Negative closing alert */}
                {module === "budgets" && currentClosingNumeric() !== null && currentClosingNumeric() < 0 && (
                  <div className="border border-rose-400 bg-rose-50 text-rose-900 rounded-md p-4 flex items-start gap-3" data-testid="report-negative-balance-alert">
                    <ShieldWarning size={20} weight="fill" className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-heading font-bold tracking-tight text-sm">Negative closing balance — {data.summary["Closing Balance"]}</div>
                      <div className="text-xs mt-0.5 opacity-80">Funds out exceed funds in for this period. This negative balance carries over as the next period's opening until an Additional Fund is recorded.</div>
                    </div>
                  </div>
                )}
                {/* Summary */}
                <div className="border border-border rounded-md bg-card">
                  <div className="p-4 border-b border-border">
                    <div className="section-caption">Summary</div>
                    <div className="font-heading font-bold tracking-tight" data-testid="report-title">{data.title}</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
                    {Object.entries(data.summary || {}).map(([kk, v]) => {
                      const neg = (kk === "Closing Balance" || kk === "Beginning Balance") && /-/.test(String(v));
                      return (
                        <div key={kk} className={`p-4 ${neg ? "bg-rose-50" : ""}`}>
                          <div className={`text-[10px] uppercase tracking-widest ${neg ? "text-rose-700" : "text-muted-foreground"}`}>{kk}</div>
                          <div className={`font-heading font-bold tabular-nums mt-1 text-lg ${neg ? "text-rose-700" : ""}`} data-testid={`summary-${kk.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Category Trend — Monthly tab only, Admin/Finance. Renders */}
                {/* top-5 expense categories × last 6 months so drift is visible */}
                {/* at a glance next to the Category Breakdown tables below. */}
                {k.key === "monthly" && trend && !trend.empty && (user?.role === "admin" || user?.role === "finance") && (
                  <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="category-trend-card">
                    <div className="p-4 border-b border-border flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="section-caption">Category Trend</div>
                        <div className="font-heading font-bold tracking-tight">Top {trend.series.length} · last {trend.months.length} months</div>
                      </div>
                      <div className="text-xs text-muted-foreground">OR line items + CV totals · ₱</div>
                    </div>
                    <div className="p-4">
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart
                          data={trend.months.map((m, i) => {
                            const row = { month: m.slice(2).replace("-", "/") };
                            for (const s of trend.series) row[s.label] = s.values[i];
                            return row;
                          })}
                          margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                          <RTooltip
                            formatter={(v) => `₱${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                            contentStyle={{ fontSize: 12, borderRadius: 6 }}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          {trend.series.map((s, i) => (
                            <Bar
                              key={s.label}
                              dataKey={s.label}
                              fill={["#18181B", "#4F46E5", "#059669", "#DB2777", "#D97706", "#0891B2", "#7C3AED", "#DC2626", "#65A30D", "#0369A1"][i % 10]}
                              radius={[2, 2, 0, 0]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Sections */}
                {(data.sections || []).map((sec, idx) => (
                  <div key={idx} className="border border-border rounded-md bg-card overflow-hidden" data-testid={`report-section-${idx}`}>
                    <div className="p-4 border-b border-border">
                      <div className="section-caption">{sec.title}</div>
                      <div className="font-heading font-bold tracking-tight">
                        {sec.layout === "two_col_ledger" ? "Funds In vs Funds Out" : `${sec.rows?.length || 0} records`}
                      </div>
                    </div>
                    {sec.layout === "two_col_ledger" && sec.rows?.length ? (
                      <div className="grid grid-cols-2 divide-x divide-border">
                        {[0, 2].map((colOffset) => (
                          <div key={colOffset} className="p-4">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                              {colOffset === 0 ? "Funds In" : "Funds Out"}
                            </div>
                            <div className="space-y-2">
                              {sec.rows.map((row, i) => {
                                const label = row[colOffset];
                                const amount = row[colOffset + 1];
                                if (!label) return null;
                                const isTotal = String(label).startsWith("TOTAL");
                                const isClosing = String(label).startsWith("CLOSING");
                                return (
                                  <div
                                    key={i}
                                    className={`flex justify-between items-baseline gap-4 py-1.5 ${
                                      isTotal || isClosing ? "border-t border-border pt-2 mt-1" : ""
                                    }`}
                                    data-testid={`cp-${colOffset === 0 ? "in" : "out"}-row-${i}`}
                                  >
                                    <span className={`text-sm ${isTotal || isClosing ? "font-heading font-bold uppercase tracking-widest text-[11px]" : "text-foreground"}`}>
                                      {label}
                                    </span>
                                    <span className={`tabular-nums ${isTotal || isClosing ? "font-heading font-bold text-lg" : "text-sm text-foreground"} ${isClosing ? "text-emerald-700" : ""}`}>
                                      {amount ? `₱${amount}` : ""}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : sec.rows?.length ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              {sec.headers.map((h, i) => (
                                <th key={i} className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sec.rows.map((row, i) => (
                              <tr key={i} className="border-b border-border last:border-0">
                                {row.map((c, j) => (
                                  <td key={j} className={`px-4 py-2 ${j === 0 ? "font-mono text-xs" : ""}`}>{c}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-6 text-sm text-muted-foreground">No records in this period.</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
